/**
 * Core utilities for filtering table columns based on saved views
 * This module provides reusable functions for any table implementation
 */

import { TblCol, TblSection } from '../table.types';
import { LegacyTableView } from '../../table/table-view.types';
import { TblSectionSpec } from '../spec';

export type FilteredColumns = {
    columns: TblCol[];
    sections: TblSection[];
};

export type ColumnSectionMapping = {
    [sectionTitle: string]: string[]; // section title -> column keys
};

/**
 * Creates a mapping of section titles to their column keys based on original section specifications
 * This is more accurate than calculating from spans since we have the exact column lists
 * @param sectionSpecs - The original section specifications with explicit column lists
 * @returns Mapping of section title to column keys
 */
export function createSectionColumnMappingFromSpecs(sectionSpecs: TblSectionSpec[]): ColumnSectionMapping {
    const mapping: ColumnSectionMapping = {};

    sectionSpecs.forEach(spec => {
        mapping[spec.title] = [...spec.cols]; // Copy the column array
    });

    return mapping;
}

/**
 * Converts TblSectionSpec[] to TblSection[] for use with column filtering
 * @param sectionSpecs - The original section specifications with column lists
 * @returns Simple sections with title and span calculated from column count
 */
export function convertSectionSpecs(sectionSpecs: TblSectionSpec[]): TblSection[] {
    return sectionSpecs.map(spec => ({
        title: spec.title,
        span: spec.cols.length,
    }));
}

/**
 * Extracts column names from a saved view's column specification
 * Handles both pipe-delimited format ("|col1|col2|") and array format
 */
export function parseViewColumns(view: LegacyTableView): string[] {
    if (!view.cols) return [];

    // Handle pipe-delimited format: "|col1|col2|col3|"
    if (typeof view.cols === 'string') {
        return view.cols.split('|').filter((col: string) => col.trim().length > 0);
    }

    // Handle array format (if view format changes in future)
    if (Array.isArray(view.cols)) {
        return view.cols;
    }

    return [];
}

/**
 * Creates a mapping of section titles to their column keys based on original columns and sections
 * This helps us understand which columns belong to which sections
 */
export function createSectionColumnMapping(allColumns: TblCol[], allSections: TblSection[]): ColumnSectionMapping {
    const mapping: ColumnSectionMapping = {};

    if (!allSections || allSections.length === 0) {
        return mapping;
    }

    // Calculate which columns belong to each section based on spans
    let columnIndex = 0;

    for (const section of allSections) {
        const sectionColumns: string[] = [];
        const endIndex = Math.min(columnIndex + section.span, allColumns.length);

        for (let i = columnIndex; i < endIndex; i++) {
            if (allColumns[i]) {
                sectionColumns.push(allColumns[i].key);
            }
        }

        mapping[section.title] = sectionColumns;
        columnIndex = endIndex;
    }

    return mapping;
}

/**
 * Filters columns array to only include those specified in the current view
 * @param allColumns - All available columns for the table
 * @param currentView - The currently selected view
 * @returns Filtered array of columns in the same order as the view specifies
 */
export function filterColumns(allColumns: TblCol[], currentView: LegacyTableView | null): TblCol[] {
    if (!currentView) {
        // No view selected - show all columns
        return allColumns;
    }

    const viewColumnNames = parseViewColumns(currentView);
    if (viewColumnNames.length === 0) {
        // View has no columns specified - show all columns
        return allColumns;
    }

    // Create a map for quick column lookup by key
    const columnMap = new Map<string, TblCol>();
    allColumns.forEach(col => {
        columnMap.set(col.key, col);
    });

    // Return columns in the order specified by the view
    const filteredColumns: TblCol[] = [];
    viewColumnNames.forEach(colName => {
        const column = columnMap.get(colName);
        if (column) {
            filteredColumns.push(column);
        }
    });

    return filteredColumns;
}

/**
 * Adjusts section spans and filters out empty sections based on visible columns
 * @param sections - Array of table sections OR section specifications
 * @param visibleColumns - Array of visible column keys
 * @param sectionMapping - Optional mapping of section titles to column keys (used for TblSection[])
 * @returns Adjusted sections with correct spans and empty sections removed
 */
export function adjustSections(
    sections: TblSection[],
    visibleColumns: string[],
    sectionMapping: ColumnSectionMapping,
): TblSection[];
export function adjustSections(sections: TblSectionSpec[], visibleColumns: string[]): TblSection[];
export function adjustSections(
    sections: TblSection[] | TblSectionSpec[],
    visibleColumns: string[],
    sectionMapping?: ColumnSectionMapping,
): TblSection[] {
    const visibleColumnSet = new Set(visibleColumns);

    // Handle TblSectionSpec[] case
    if (sections.length > 0 && 'cols' in sections[0]) {
        const sectionSpecs = sections as TblSectionSpec[];
        const result = sectionSpecs
            .map(spec => {
                const visibleSectionColumns = spec.cols.filter(col => visibleColumnSet.has(col));

                return {
                    title: spec.title,
                    span: visibleSectionColumns.length,
                };
            })
            .filter(section => section.span > 0); // Remove sections with no visible columns

        return result;
    }

    // Handle TblSection[] case (existing logic)
    const tblSections = sections as TblSection[];
    if (!sectionMapping) {
        throw new Error('sectionMapping is required when working with TblSection[]');
    }

    return tblSections
        .map(section => {
            const sectionColumns = sectionMapping[section.title] || [];
            const visibleSectionColumns = sectionColumns.filter(col => visibleColumnSet.has(col));

            return {
                ...section,
                span: visibleSectionColumns.length,
            };
        })
        .filter(section => section.span > 0); // Remove sections with no visible columns
}

/**
 * Main function that filters both columns and sections based on a saved view
 * @param allColumns - All available columns for the table
 * @param allSections - All available sections for the table
 * @param currentView - The currently selected view
 * @returns Object with filtered columns and sections
 */
export function filterTableByView(
    allColumns: TblCol[],
    allSections?: TblSection[],
    view?: LegacyTableView,
): FilteredColumns {
    if (!view || !view.cols) {
        return {
            columns: allColumns,
            sections: allSections || [],
        };
    }

    const viewColumnKeys = new Set(parseViewColumns(view));
    const filteredColumns = allColumns.filter(col => viewColumnKeys.has(col.key));

    if (!allSections || allSections.length === 0) {
        return {
            columns: filteredColumns,
            sections: [],
        };
    }

    // Create mapping of section titles to column keys
    const mapping = createSectionColumnMapping(allColumns, allSections);

    // Adjust sections based on filtered columns
    const adjustedSections = adjustSections(
        allSections,
        filteredColumns.map(col => col.key),
        mapping,
    );

    return {
        columns: filteredColumns,
        sections: adjustedSections,
    };
}

/**
 * Finds a view by ID in an array of views
 * @param views - Array of available views
 * @param viewId - ID of the view to find
 * @returns The matching view or null if not found
 */
export function findViewById(views: LegacyTableView[], viewId: string): LegacyTableView | null {
    return views.find(view => view.id === viewId) || null;
}
